import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardPaste, Copy, Scissors, TextSelect } from 'lucide-react';
import ContextMenu from './ContextMenu';
import { resolveTextMenu } from '../../utils/textContextMenuModel';
import { copyAction, cutAction, pasteAction, selectAllAction } from '../../utils/textContextActions';
import { clipboardHasText } from '../../utils/clipboardMenu';

/**
 * The app-wide fallback right-click menu: Cut / Copy / Paste / Select all for
 * selected text and text fields, in the app's own ContextMenu look.
 *
 * It owns the ONE document-level `contextmenu` listener that keeps the
 * webview's native menu away (it used to be a bare preventDefault in App.js),
 * and decides per event:
 *
 *  - Already `defaultPrevented` → a component's own onContextMenu claimed this
 *    right-click (collection tree, tabs, graph, DB grid, "To Variable", …).
 *    Leave it alone. This is reliable because the listener is on `document` in
 *    the BUBBLE phase: React 19 dispatches its synthetic handlers from
 *    listeners on the root container (and on each portal container, e.g.
 *    <body>), both of which the event passes before it reaches `document`.
 *    Native listeners on elements below (GraphCanvas' canvas) run earlier
 *    still.
 *  - Otherwise the native menu is suppressed as before, and this menu opens
 *    only if `resolveTextMenu` finds something to offer. Monaco and xterm are
 *    opted out there and keep their own behaviour.
 */
export default function TextContextMenu() {
  const { t } = useTranslation();
  const [menu, setMenu] = useState(null);
  const close = useCallback(() => setMenu(null), []);

  useEffect(() => {
    const onContextMenu = (e) => {
      if (e.defaultPrevented) return;
      e.preventDefault();
      const model = resolveTextMenu(e.target, window.getSelection?.() ?? null);
      setMenu(model ? { x: e.clientX, y: e.clientY, model, canPaste: true } : null);
    };
    document.addEventListener('contextmenu', onContextMenu);
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, []);

  // Grey out Paste when the clipboard is readable without a prompt and empty.
  const pasteShown = Boolean(menu?.model.actions.paste);
  useEffect(() => {
    if (!pasteShown) return undefined;
    let live = true;
    clipboardHasText().then((ok) => {
      if (live && !ok) setMenu((m) => (m ? { ...m, canPaste: false } : m));
    });
    return () => {
      live = false;
    };
  }, [pasteShown, menu?.x, menu?.y]);

  // Close when the text it is about moves (scroll) or the window loses focus.
  // Escape and outside clicks are handled by ContextMenu itself.
  const anchor = menu ? menu.model.el : null;
  const open = Boolean(menu);
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => {
      const node = e.target;
      if (node === document || node === window || !anchor || node?.contains?.(anchor)) close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('blur', close);
    };
  }, [open, anchor, close]);

  if (!menu) return null;
  const { model } = menu;
  const { actions } = model;
  const row = (key, enabled, label, icon, run) =>
    key in actions && {
      icon,
      label,
      disabled: !enabled,
      onClick: () => {
        Promise.resolve(run(model)).catch(() => {});
      },
    };

  const items = [
    row('cut', actions.cut, t('textMenu.cut'), <Scissors size={14} />, cutAction),
    row('copy', actions.copy, t('textMenu.copy'), <Copy size={14} />, copyAction),
    row('paste', actions.paste && menu.canPaste, t('textMenu.paste'), <ClipboardPaste size={14} />, pasteAction),
    row('selectAll', actions.selectAll, t('textMenu.selectAll'), <TextSelect size={14} />, selectAllAction),
  ];

  return (
    <ContextMenu
      x={menu.x}
      y={menu.y}
      items={items}
      onClose={close}
      className="text-context-menu"
      // Above the expanded URL overlay (100000), like the "To Variable" menu.
      zIndex={100001}
      preserveFocus
    />
  );
}
